import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  TrendingUp, 
  Clock, 
  DollarSign,
  Activity,
  CheckCircle2,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { format } from 'date-fns';

interface LiveBet {
  id: string;
  match_id: string;
  bet_type: string;
  selection: string;
  odds: number;
  stake: number;
  potential_win: number;
  status: string;
  placed_at: string;
  settled_at: string | null;
  result: string | null;
  live_matches?: {
    home_team: string;
    away_team: string;
    home_score: number;
    away_score: number;
    current_minute: number;
    is_live: boolean;
  };
}

export function MyLiveBets() {
  const { user } = useAuth();
  const [bets, setBets] = useState<LiveBet[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    won: 0,
    lost: 0,
    totalStake: 0,
    totalWinnings: 0
  });

  useEffect(() => {
    if (user) {
      fetchBets();
      setupRealtimeSubscription();
    }
  }, [user]);

  const fetchBets = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('live_bets')
        .select(`
          *,
          live_matches (
            home_team,
            away_team,
            home_score,
            away_score,
            current_minute,
            is_live
          )
        `)
        .eq('user_id', user.id)
        .order('placed_at', { ascending: false });

      if (error) throw error;

      const betsData = data || [];
      setBets(betsData);

      // Calculate stats
      setStats({
        total: betsData.length,
        pending: betsData.filter(b => b.status === 'pending').length,
        won: betsData.filter(b => b.status === 'won').length,
        lost: betsData.filter(b => b.status === 'lost').length,
        totalStake: betsData.reduce((sum, b) => sum + Number(b.stake), 0),
        totalWinnings: betsData
          .filter(b => b.status === 'won')
          .reduce((sum, b) => sum + Number(b.potential_win), 0)
      });
    } catch (error) {
      console.error('Error fetching bets:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel('my-live-bets')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'live_bets',
          filter: `user_id=eq.${user?.id}`
        },
        (payload) => {
          if (payload.eventType === 'UPDATE') {
            setBets(prev => prev.map(b => 
              b.id === payload.new.id ? { ...b, ...payload.new } as LiveBet : b
            ));
            fetchBets(); // Refresh to update stats
          } else if (payload.eventType === 'INSERT') {
            fetchBets();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'won':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'lost':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'won':
        return 'bg-green-500/20 text-green-700 border-green-500/30';
      case 'lost':
        return 'bg-red-500/20 text-red-700 border-red-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-700 border-yellow-500/30';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  if (!user) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Activity className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Sign in to view your live bets</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>My Live Bets</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-muted rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <Card className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-6 w-6 text-blue-500" />
            My Live Bets
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-card rounded-lg">
              <div className="text-2xl font-bold text-foreground">{stats.total}</div>
              <div className="text-xs text-muted-foreground">Total Bets</div>
            </div>
            <div className="text-center p-4 bg-card rounded-lg">
              <div className="text-2xl font-bold text-yellow-500">{stats.pending}</div>
              <div className="text-xs text-muted-foreground">Pending</div>
            </div>
            <div className="text-center p-4 bg-card rounded-lg">
              <div className="text-2xl font-bold text-foreground">${stats.totalStake.toFixed(2)}</div>
              <div className="text-xs text-muted-foreground">Total Staked</div>
            </div>
            <div className="text-center p-4 bg-card rounded-lg">
              <div className="text-2xl font-bold text-green-500">
                ${stats.totalWinnings.toFixed(2)}
              </div>
              <div className="text-xs text-muted-foreground">Total Won</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bets List */}
      {bets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Activity className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No live bets yet</p>
            <p className="text-sm text-muted-foreground">Place your first bet on a live match!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {bets.map((bet) => (
            <Card 
              key={bet.id}
              className={`transition-all duration-300 ${
                bet.live_matches?.is_live ? 'border-red-500/30 bg-gradient-to-r from-red-500/5 to-orange-500/5' : ''
              }`}
            >
              <CardContent className="p-6">
                <div className="space-y-4">
                  {/* Bet Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {bet.live_matches && (
                        <div className="font-bold text-lg mb-1">
                          {bet.live_matches.home_team} vs {bet.live_matches.away_team}
                        </div>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-xs">
                          {bet.bet_type}
                        </Badge>
                        <Badge className={getStatusColor(bet.status)}>
                          <span className="flex items-center gap-1">
                            {getStatusIcon(bet.status)}
                            {bet.status.toUpperCase()}
                          </span>
                        </Badge>
                        {bet.live_matches?.is_live && (
                          <Badge className="bg-red-500 text-white animate-pulse">
                            <Activity className="h-3 w-3 mr-1" />
                            LIVE {bet.live_matches.current_minute}'
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-primary">
                        {bet.odds.toFixed(2)}
                      </div>
                      <div className="text-xs text-muted-foreground">odds</div>
                    </div>
                  </div>

                  {/* Live Score */}
                  {bet.live_matches?.is_live && (
                    <div className="flex items-center justify-center gap-4 py-3 px-4 bg-muted/50 rounded-lg">
                      <div className="text-center">
                        <div className="text-sm text-muted-foreground mb-1">
                          {bet.live_matches.home_team}
                        </div>
                        <div className="text-3xl font-bold">{bet.live_matches.home_score}</div>
                      </div>
                      <div className="text-muted-foreground font-semibold">-</div>
                      <div className="text-center">
                        <div className="text-sm text-muted-foreground mb-1">
                          {bet.live_matches.away_team}
                        </div>
                        <div className="text-3xl font-bold">{bet.live_matches.away_score}</div>
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Bet Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Selection</div>
                      <div className="font-semibold">{bet.selection}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Stake</div>
                      <div className="font-semibold flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        {bet.stake.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Potential Win</div>
                      <div className="font-semibold text-green-500 flex items-center gap-1">
                        <TrendingUp className="h-4 w-4" />
                        ${bet.potential_win.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Placed</div>
                      <div className="font-semibold text-sm">
                        {format(new Date(bet.placed_at), 'HH:mm')}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
